/**
 * localStorage-backed repository for the service-mapping CMS.
 *
 * SAFETY: this module is the only persistence path, and it writes exclusively to
 * localStorage under the `svc-cms:` prefix. It imports no Supabase or Directus
 * client and makes no network calls.
 *
 * One feature-level exception, and it does not live here: image FILE BYTES are
 * uploaded to a Supabase storage bucket, because they cannot fit in the ~5MB
 * localStorage quota. Only the resulting public URL is written back through
 * this module, as an ordinary string field. See components/ImageField.tsx.
 *
 * Every mutation returns a NEW store object so React state updates propagate.
 */

import { localeUsage, localeRemovalBlocker } from "../shared/localeUsage"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import { ulid } from "ulid"
import {
  type ServiceStore, type EntityKind, type Language, type Instance, type Country,
  type Region, type City, type ProviderCategory, type ServiceCategory,
  type Tag, type Population, type Accessibility, type Provider,
  type Service, type Translated, BUCKET, emptyServiceStore, displayName,
  serviceStatusFor,
} from "./types"

// A language the Service Map doesn't list yet has to be one the sites can show
function assertNewLocaleSupported(store: ServiceStore, locale: string) {
  if (!store.languages[locale] && !isSiteLocale(locale)) throw new Error(unsupportedLocaleMessage(locale))
}

const KEY_PREFIX = "svc-cms:"
const DEFAULT_DB = "default"

function keyFor(id: string) { return `${KEY_PREFIX}${id}` }
function now() { return Date.now() }
function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) }

// A new store sharing every record except the one being changed, which is copied.
// Unchanged records keep their identity, so saving and staging can skip them without comparing
function withRecord(store: ServiceStore, kind: EntityKind, id: string): { next: ServiceStore; bucket: Record<string, any>; entity: any } {
  const key = BUCKET[kind]
  const bucket = { ...(store[key] as Record<string, any>) }
  const next = { ...store, [key]: bucket } as ServiceStore
  const entity = bucket[id] ? clone(bucket[id]) : undefined
  if (entity) bucket[id] = entity
  return { next, bucket, entity }
}

/**
 * A fresh, empty store.
 *
 * There is no local cache: the hook pulls the real store from Postgres on
 * mount and on every team switch, so anything held here would be a third
 * copy of state that React and the store ref already own -- and one that
 * silently went stale on a reload.
 */
export function emptyStoreFor(): ServiceStore {
  return emptyServiceStore()
}

// ── Generic access ───────────────────────────────────────────────────────────

export function getEntity(store: ServiceStore, kind: EntityKind, id: string): any | null {
  const bucket = store[BUCKET[kind]] as Record<string, any>
  return bucket?.[id] ?? null
}

export function listEntities(store: ServiceStore, kind: EntityKind): any[] {
  const bucket = store[BUCKET[kind]] as Record<string, any>
  return Object.values(bucket ?? {})
}

export function countEntities(store: ServiceStore, kind: EntityKind): number {
  return Object.keys((store[BUCKET[kind]] as Record<string, any>) ?? {}).length
}

/** Language codes are their own primary key in Directus, so no ULID here. */
export function upsertLanguage(store: ServiceStore, lang: Language): ServiceStore {
  assertNewLocaleSupported(store, lang.code)
  const next = clone(store)
  next.languages[lang.code] = { ...lang, direction: lang.direction ?? "ltr" }
  return next
}

function mkTranslated(locale: string, name: string, extra: Partial<Translated> = {}): Translated {
  return { locale, name, updatedAt: now(), ...extra }
}

/**
 * Creates an entity in the given bucket.
 *
 * `data` supplies the entity's own fields; id/timestamps/translations are
 * managed here so every entity is consistent.
 */
export function createEntity(
  store: ServiceStore,
  kind: EntityKind,
  data: Record<string, any>,
  opts: { locale?: string } = {}
): { store: ServiceStore; id: string } {
  const id = ulid()
  const { next, bucket: target } = withRecord(store, kind, id)
  const locale = opts.locale || next.defaultLocale

  const base: any = {
    id,
    createdAt: now(),
    updatedAt: now(),
    translations: data.name
      ? { [locale]: mkTranslated(locale, data.name, {
          description: data.description,
          address: data.address,
          definition: data.definition,
        }) }
      : {},
  }

  // Array fields default to empty so consumers never guard for undefined.
  if (kind === "service") {
    Object.assign(base, {
      status: "draft",
      categoryIds: [], tagIds: [], populationIds: [], accessibilityIds: [],
      hours: [], contactInfo: [], consentForms: [],
    })
  }
  if (kind === "serviceCategory") Object.assign(base, { status: "draft" })
  if (kind === "country") Object.assign(base, { languageCodes: [] })
  if (kind === "provider") Object.assign(base, { status: "Active" })

  target[id] = { ...base, ...data }
  return { store: next, id }
}

export function updateEntity(
  store: ServiceStore,
  kind: EntityKind,
  id: string,
  patch: Record<string, any>
): ServiceStore {
  const { next, entity } = withRecord(store, kind, id)
  if (!entity) throw new Error(`No such ${kind}: ${id}`)
  // Never let a patch clobber identity or creation time.
  const { id: _i, createdAt: _c, translations: _t, ...safe } = patch
  Object.assign(entity, safe, { updatedAt: now() })
  return next
}

/** Upserts one locale's translation on any translatable entity. */
export function setTranslation(
  store: ServiceStore,
  kind: EntityKind,
  id: string,
  locale: string,
  patch: Partial<Omit<Translated, "locale" | "updatedAt">>
): ServiceStore {
  assertNewLocaleSupported(store, locale)
  const { next, entity } = withRecord(store, kind, id)
  if (!entity) throw new Error(`No such ${kind}: ${id}`)
  entity.translations ||= {}
  const existing = entity.translations[locale]
  entity.translations[locale] = {
    locale,
    name: patch.name ?? existing?.name ?? "",
    description: patch.description ?? existing?.description,
    address: patch.address ?? existing?.address,
    definition: patch.definition ?? existing?.definition,
    reviewed: patch.reviewed ?? existing?.reviewed,
    status: patch.status ?? existing?.status,
    updatedAt: now(),
  }
  entity.updatedAt = now()
  return next
}

/** One translation write, for setTranslationsBulk. */
export interface TranslationWrite {
  kind: EntityKind
  id: string
  locale: string
  patch: Partial<Omit<Translated, "locale" | "updatedAt">>
}

/**
 * Applies many translation writes with ONE clone of the store.
 *
 * setTranslation deep-clones the whole store per call, so a batch of N writes
 * costs O(N x storeBytes) — seconds of blocked main thread once a dataset has a
 * few hundred services. The batch tools (svc_copy_locale, svc_translate_batch)
 * write many entities at once and only ever commit the final store, so cloning
 * per write buys nothing.
 *
 * Each write's `onBefore` receives a deep copy of that entity as it was
 * immediately BEFORE its write. That matters: the history layer reads its
 * `before` snapshot lazily from the store it is handed, and a single shared
 * mutable store would hand it post-write state, silently corrupting revert.
 *
 * Writes naming a missing entity are skipped and reported, rather than
 * throwing and losing the whole batch.
 */
export function setTranslationsBulk(
  store: ServiceStore,
  writes: TranslationWrite[],
  onBefore?: (write: TranslationWrite, before: Record<string, any>) => void,
): { store: ServiceStore; applied: number; missing: TranslationWrite[] } {
  const next = clone(store)
  const missing: TranslationWrite[] = []
  let applied = 0

  for (const w of writes) {
    assertNewLocaleSupported(next, w.locale)
    const bucket = next[BUCKET[w.kind]] as Record<string, any>
    const entity = bucket[w.id]
    if (!entity) { missing.push(w); continue }

    onBefore?.(w, clone(entity))

    entity.translations ||= {}
    const existing = entity.translations[w.locale]
    entity.translations[w.locale] = {
      locale: w.locale,
      name: w.patch.name ?? existing?.name ?? "",
      description: w.patch.description ?? existing?.description,
      address: w.patch.address ?? existing?.address,
      definition: w.patch.definition ?? existing?.definition,
      reviewed: w.patch.reviewed ?? existing?.reviewed,
      status: w.patch.status ?? existing?.status,
      updatedAt: now(),
    }
    entity.updatedAt = now()
    applied++
  }

  return { store: next, applied, missing }
}

// ── Referential integrity ────────────────────────────────────────────────────

/**
 * Everything that points at `id`, so a delete can report (or refuse) rather
 * than silently orphaning rows.
 *
 * Directus sets most of these FKs to SET NULL on delete; we surface them so the
 * caller decides. Deleting a country in particular cascades widely.
 */
export function findReferences(store: ServiceStore, kind: EntityKind, id: string): Array<{ kind: EntityKind; id: string; field: string }> {
  const refs: Array<{ kind: EntityKind; id: string; field: string }> = []
  const add = (k: EntityKind, entityId: string, field: string) => refs.push({ kind: k, id: entityId, field })

  if (kind === "country") {
    for (const r of Object.values(store.regions)) if (r.countryId === id) add("region", r.id, "countryId")
    for (const c of Object.values(store.cities)) if (c.countryId === id) add("city", c.id, "countryId")
    for (const p of Object.values(store.providers)) if (p.countryId === id) add("provider", p.id, "countryId")
    for (const s of Object.values(store.services)) if (s.countryId === id) add("service", s.id, "countryId")
    for (const pc of Object.values(store.providerCategories)) if (pc.countryId === id) add("providerCategory", pc.id, "countryId")
  }
  if (kind === "region") {
    for (const c of Object.values(store.cities)) if (c.regionId === id) add("city", c.id, "regionId")
    for (const s of Object.values(store.services)) if (s.regionId === id) add("service", s.id, "regionId")
  }
  if (kind === "city") {
    for (const s of Object.values(store.services)) if (s.cityId === id) add("service", s.id, "cityId")
  }
  if (kind === "provider") {
    for (const s of Object.values(store.services)) if (s.providerId === id) add("service", s.id, "providerId")
  }
  if (kind === "providerCategory") {
    for (const p of Object.values(store.providers)) if (p.categoryId === id) add("provider", p.id, "categoryId")
  }
  if (kind === "serviceCategory") {
    for (const s of Object.values(store.services)) if (s.categoryIds.includes(id)) add("service", s.id, "categoryIds")
  }
  if (kind === "tag") {
    for (const s of Object.values(store.services)) if (s.tagIds.includes(id)) add("service", s.id, "tagIds")
  }
  if (kind === "population") {
    for (const s of Object.values(store.services)) if (s.populationIds.includes(id)) add("service", s.id, "populationIds")
  }
  if (kind === "accessibility") {
    for (const s of Object.values(store.services)) if (s.accessibilityIds.includes(id)) add("service", s.id, "accessibilityIds")
  }
  if (kind === "instance") {
    for (const c of Object.values(store.countries)) if (c.instanceId === id) add("country", c.id, "instanceId")
  }
  if (kind === "language") {
    for (const c of Object.values(store.countries)) {
      if (c.defaultLanguage === id) add("country", c.id, "defaultLanguage")
      else if (c.languageCodes.includes(id)) add("country", c.id, "languageCodes")
    }
  }

  return refs
}

/**
 * Deletes an entity, nulling scalar FKs and pruning id arrays that pointed at
 * it — mirroring Directus's SET NULL behaviour instead of leaving dangling ids.
 */
/** Every translatable entity, for locale-usage checks. */
function allTranslatable(store: ServiceStore) {
  return [
    ...Object.values(store.countries), ...Object.values(store.regions),
    ...Object.values(store.cities), ...Object.values(store.providerCategories),
    ...Object.values(store.serviceCategories), ...Object.values(store.tags),
    ...Object.values(store.populations), ...Object.values(store.accessibility),
    ...Object.values(store.providers), ...Object.values(store.services),
  ]
}

/** Why `locale` cannot be removed, or null when it can. */
export function localeRemovalError(store: ServiceStore, locale: string): string | null {
  const usage = localeUsage(
    allTranslatable(store),
    locale,
    (e: any) => e.name || e.id,
    store.defaultLocale,
  )
  return localeRemovalBlocker(usage, locale)
}

export function deleteEntity(
  store: ServiceStore,
  kind: EntityKind,
  id: string
): { store: ServiceStore; detached: number } {
  const next = clone(store)
  const bucket = next[BUCKET[kind]] as Record<string, any>
  if (!bucket[id]) throw new Error(`No such ${kind}: ${id}`)

  /*
   * Languages are not referenced by FK -- entities carry their translations
   * keyed by locale code -- so findReferences cannot see them. Without this
   * check, deleting a language leaves every translation in place but
   * unreachable through the UI.
   */
  if (kind === "language") {
    const blocker = localeRemovalError(next, bucket[id].code)
    if (blocker) throw new Error(blocker)
  }

  const refs = findReferences(next, kind, id)
  for (const ref of refs) {
    const target = (next[BUCKET[ref.kind]] as Record<string, any>)[ref.id]
    if (!target) continue
    if (Array.isArray(target[ref.field])) {
      target[ref.field] = target[ref.field].filter((x: string) => x !== id)
    } else {
      target[ref.field] = undefined
    }
    target.updatedAt = now()
  }

  delete bucket[id]
  return { store: next, detached: refs.length }
}

/*
 * The read-only views live in ./queries.ts but are re-exported here, because
 * every caller imports this module as a namespace (`db.searchStore(...)`).
 * Re-exporting keeps that surface unchanged, so splitting the file did not
 * require touching a single call site.
 */
export * from "./queries"
